import i18n from '../../i18n';
// frontend/src/redux/reducers/schemaReducer.js

const initialState = {
    // 연결 목록의 유일한 소스는 backend 의 GET /connections 다 (App.js 가 SET_CONNECTIONS 로 주입).
    // 예전에는 웹 모드에 한해 'Oracle SQL 실습 DB'(id: conn_oracle_local_1) 라는 플레이스홀더를
    // 넣어 두었는데, 백엔드에 존재하지 않는 id 라서
    //   - 앱 시작 직후 이 항목으로 스키마 자동 조회가 나가 "Connection config not found" 로 실패하고
    //   - 그 실패가 schemasError 에 남아 연결명과 Schemas 노드에 (Error) 로 표시되며
    //   - autoConnectAttempted 플래그를 소진해 진짜 첫 연결의 스키마 자동 로드를 막았다.
    // 빈 배열로 시작하고, 연결이 없을 때의 안내는 SchemaExplorer 의 빈 상태 UI 가 담당한다.
    connections: [],
    // 테이블/컬럼 주석 매핑 (물리명 → 주석)
    // tableComments: { "SCHEMA.TABLE": "주석", "TABLE": "주석" }
    // columnComments: { "SCHEMA.TABLE": { "COL": "주석" }, "TABLE": { "COL": "주석" } }
    tableComments: {},
    columnComments: {},
    isLoadingComments: false,
};

// 나중에 추가될 Action Types (임시)
const FETCH_SCHEMAS_START = 'FETCH_SCHEMAS_START';
const FETCH_SCHEMAS_SUCCESS = 'FETCH_SCHEMAS_SUCCESS';
const FETCH_SCHEMAS_FAILURE = 'FETCH_SCHEMAS_FAILURE';
const INVALIDATE_SCHEMAS = 'INVALIDATE_SCHEMAS';
const SET_CONNECTIONS = 'SET_CONNECTIONS'; // 서버에서 connection 목록을 가져올 경우
const FETCH_COMMENTS_START = 'FETCH_COMMENTS_START';
const FETCH_COMMENTS_SUCCESS = 'FETCH_COMMENTS_SUCCESS';
const FETCH_COMMENTS_FAILURE = 'FETCH_COMMENTS_FAILURE';
const DISCONNECT_CONNECTION = 'DISCONNECT_CONNECTION';
const REMOVE_CONNECTION = 'REMOVE_CONNECTION';
const RECONNECT_CONNECTION = 'RECONNECT_CONNECTION';

const schemaReducer = (state = initialState, action) => {
    switch (action.type) {
        case SET_CONNECTIONS:
            return {
                ...state,
                connections: action.payload.map(conn => ({
                    ...conn,
                    children: conn.children || null,
                    isConnected: conn.isConnected !== undefined ? conn.isConnected : true,
                    isLoadingSchemas: false,
                    schemasError: null,
                })),
            };
        case FETCH_SCHEMAS_START:
            return {
                ...state,
                connections: state.connections.map(conn =>
                    conn.id === action.payload.connectionId
                        ? { ...conn, isLoadingSchemas: true, schemasError: null }
                        : conn
                ),
            };
        case INVALIDATE_SCHEMAS:
            // 새로고침: children 비우고 lastFetched 초기화. 다음 fetchSchemas 가 reducer 가드를 통과해 정상 적용됨.
            return {
                ...state,
                connections: state.connections.map(conn =>
                    conn.id === action.payload.connectionId
                        ? { ...conn, children: null, schemasError: null }
                        : conn
                ),
            };
        case FETCH_SCHEMAS_SUCCESS:
            // 이미 스키마가 로드되었다면 중복 업데이트 방지
            const targetConnection = state.connections.find(conn => conn.id === action.payload.connectionId);
            if (targetConnection && targetConnection.children && targetConnection.children.length > 0) {
                console.log('[Reducer] Schemas already loaded for connection:', action.payload.connectionId);
                return state;
            }
            
            const newStateSuccess = {
                ...state,
                connections: state.connections.map(conn =>
                    conn.id === action.payload.connectionId
                        ? {
                              ...conn,
                              isLoadingSchemas: false,
                              isConnected: true,
                              schemasError: null,
                              children: action.payload.schemas.map(schema => {
                                  const sid = `${conn.id}_schema_${schema.name.toLowerCase().replace(/[^a-zA-Z0-9가-힣]/g, '_')}`;
                                  const mkId = (suffix) => `${sid}_${suffix}`;
                                  const folders = [];

                                  // 테이블 폴더
                                  const tables = (schema.tables || []).map(t => ({
                                      id: mkId(`tbl_${t.name.toLowerCase().replace(/[^a-zA-Z0-9가-힣]/g, '_')}`),
                                      name: t.name, type: 'table', schemaName: schema.name, connectionId: conn.id,
                                  }));
                                  folders.push({
                                      id: mkId('folder_tables'), name: i18n.t('common:tree.tables', { n: tables.length }), type: 'folder', folderType: 'tables',
                                      schemaName: schema.name, connectionId: conn.id, children: tables,
                                  });

                                  // 뷰 폴더
                                  const views = (schema.views || []).map(v => ({
                                      id: mkId(`view_${v.name.toLowerCase().replace(/[^a-zA-Z0-9가-힣]/g, '_')}`),
                                      name: v.name, type: 'view', schemaName: schema.name, connectionId: conn.id,
                                  }));
                                  folders.push({
                                      id: mkId('folder_views'), name: i18n.t('common:tree.views', { n: views.length }), type: 'folder', folderType: 'views',
                                      schemaName: schema.name, connectionId: conn.id, children: views,
                                  });

                                  // 프로시저/함수 폴더 — overloading 대응: oid 가 있으면 id 에 같이 포함 (unique).
                                  const procs = (schema.procedures || []).map((p, idx) => ({
                                      id: mkId(`proc_${p.name.toLowerCase().replace(/[^a-zA-Z0-9가-힣]/g, '_')}_${p.oid || idx}`),
                                      name: p.args ? `${p.name}(${p.args})` : p.name,
                                      displayName: p.name,
                                      type: 'procedure', objectType: p.objectType, oid: p.oid, args: p.args,
                                      schemaName: schema.name, connectionId: conn.id,
                                  }));
                                  folders.push({
                                      id: mkId('folder_procs'), name: i18n.t('common:tree.procs', { n: procs.length }), type: 'folder', folderType: 'procedures',
                                      schemaName: schema.name, connectionId: conn.id, children: procs,
                                  });

                                  return {
                                      id: sid, name: schema.name, type: 'schema',
                                      connectionId: conn.id, children: folders,
                                  };
                              })
                          }
                        : conn
                ),
            };
            return newStateSuccess;
        case FETCH_SCHEMAS_FAILURE:
            return {
                ...state,
                connections: state.connections.map(conn =>
                    conn.id === action.payload.connectionId
                        ? { ...conn, isLoadingSchemas: false, schemasError: action.payload.error }
                        : conn
                ),
            };
        case FETCH_COMMENTS_START:
            return { ...state, isLoadingComments: true };
        case FETCH_COMMENTS_SUCCESS:
            return {
                ...state,
                isLoadingComments: false,
                tableComments: { ...state.tableComments, ...action.payload.tableComments },
                columnComments: {
                    ...state.columnComments,
                    ...Object.fromEntries(
                        Object.entries(action.payload.columnComments).map(([table, cols]) => [
                            table,
                            { ...(state.columnComments[table] || {}), ...cols }
                        ])
                    )
                },
            };
        case FETCH_COMMENTS_FAILURE:
            return { ...state, isLoadingComments: false };
        case DISCONNECT_CONNECTION:
            return {
                ...state,
                connections: state.connections.map(conn =>
                    conn.id === action.payload
                        ? { ...conn, isConnected: false, children: null, isLoadingSchemas: false, schemasError: null }
                        : conn
                ),
            };
        case RECONNECT_CONNECTION:
            return {
                ...state,
                connections: state.connections.map(conn =>
                    conn.id === action.payload
                        ? { ...conn, isConnected: true, children: null, isLoadingSchemas: false, schemasError: null }
                        : conn
                ),
            };
        case REMOVE_CONNECTION:
            return {
                ...state,
                connections: state.connections.filter(conn => conn.id !== action.payload),
            };
        default:
            return state;
    }
};

export default schemaReducer; 